import React, { useState, useEffect } from 'react';
import { 
  MdAccountBalance,
  MdCheckCircle,
  MdAccessTime,
  MdCancel,
  MdWarning
} from 'react-icons/md';
import './Withdraw.css';
import {
  getCurrentUser,
  getUserWallet,
  getUserMT5Accounts,
  createWithdrawal,
  getUserWithdrawals
} from '../../lib/supabase/helpers';
import { api } from '../../services/api';

export default function Withdraw() {
  // Form states
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('bank_transfer');
  const [bankDetails, setBankDetails] = useState({
    accountName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: ''
  });
  const [upiDetails, setUpiDetails] = useState({
    upiId: ''
  });

  // UI states
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accountLoading, setAccountLoading] = useState(false);

  // Data states
  const [currentUser, setCurrentUser] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [mt5Accounts, setMt5Accounts] = useState([]);
  const [selectedMt5Login, setSelectedMt5Login] = useState('');
  const [selectedAccountIndex, setSelectedAccountIndex] = useState(0);
  const [withdrawHistory, setWithdrawHistory] = useState([]);
  const [liveBalances, setLiveBalances] = useState({});

  const [accountInfo, setAccountInfo] = useState({
    walletBalance: 0,
    mt5Balance: 0,
    freeMargin: 0,
    openPositions: 0,
    canWithdraw: true
  });

  const minWithdrawal = 1000;

  // Load initial data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Main data fetching function
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get current user
      const user = await getCurrentUser();
      if (!user) {
        console.log('No user logged in');
        setLoading(false);
        return;
      }

      setCurrentUser(user);

      // Get wallet data
      const wallet = await getUserWallet(user.user.id);
      setWalletData(wallet);

      // Get MT5 accounts
      const accounts = await getUserMT5Accounts(user.user.id);
      setMt5Accounts(accounts || []);
      
      if (accounts && accounts.length > 0) {
        setSelectedMt5Login(accounts[0].login_id);
        await loadAccountData(accounts[0], wallet);
      } else {
        // No MT5 accounts - use only wallet balance
        setAccountInfo({
          walletBalance: parseFloat(wallet?.available_balance || 0),
          mt5Balance: 0,
          freeMargin: 0,
          openPositions: 0,
          canWithdraw: true
        });
      }

      // Get withdrawal history
      const withdrawals = await getUserWithdrawals(user.user.id);
      setWithdrawHistory(withdrawals || []);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAccountData = async (account, wallet = null) => {
  const walletBalance = parseFloat(wallet?.available_balance || walletData?.available_balance || 0);

  try {
    console.log('📊 Loading account data for:', account.login_id);
    
    const mt5Result = await api.getAccount(account.login_id);
    
    if (mt5Result.success && mt5Result.data) {
      console.log('✅ Live MT5 data loaded from API');
      
      const liveBalance = parseFloat(mt5Result.data.balance || 0);
      
      // Save the balance
      setLiveBalances(prevBalances => ({
        ...prevBalances,
        [account.login_id]: liveBalance
      }));
      
      const newAccountInfo = {
        walletBalance: walletBalance,
        mt5Balance: liveBalance,
        freeMargin: parseFloat(mt5Result.data.margin_free || 0),
        openPositions: 0,
        canWithdraw: parseFloat(mt5Result.data.margin_free || 0) >= 0
      };

      setAccountInfo(newAccountInfo);

      try {
        const positions = await api.getPositions(account.login_id);
        if (positions.success && positions.data) {
          setAccountInfo(prev => ({
            ...prev,
            openPositions: positions.data.total_positions || 0
          }));
        }
      } catch (posError) {
        console.warn('⚠️ Could not fetch positions:', posError.message);
      }

    } else {
      throw new Error('API returned no data');
    }
    
  } catch (apiError) {
    console.warn('⚠️ Using database values (API unavailable):', apiError.message);
    
    const dbBalance = parseFloat(account.balance || 0);
    
    // Save the database balance
    setLiveBalances(prevBalances => ({
      ...prevBalances,
      [account.login_id]: dbBalance
    }));
    
    setAccountInfo({
      walletBalance: walletBalance,
      mt5Balance: dbBalance,
      freeMargin: parseFloat(account.free_margin || 0),
      openPositions: 0,
      canWithdraw: parseFloat(account.free_margin || 0) >= 0
    });
  }
};

  // Handle MT5 account selection change
  const handleAccountChange = async (e) => {
    const index = parseInt(e.target.value);
    setSelectedAccountIndex(index);
    setAccountLoading(true);
    
    const selectedAccount = mt5Accounts[index];

    if (selectedAccount) {
      setSelectedMt5Login(selectedAccount.login_id);
      await loadAccountData(selectedAccount);
      setAccountLoading(false);
    } else {
      setAccountLoading(false);
    }
  };

  // Calculate maximum withdrawable amount
  const maxWithdrawal = Math.max(0, Math.min(
    accountInfo.walletBalance,
    accountInfo.freeMargin > 0 ? accountInfo.mt5Balance : accountInfo.walletBalance
  ));

  // Handle withdrawal form submission
const handleSubmit = async (e) => {
  e.preventDefault();

  if (!currentUser) {
    alert('Please login first');
    return;
  }

  const withdrawAmount = parseFloat(amount);

  // Validations
  if (isNaN(withdrawAmount) || withdrawAmount <= 0) {
    alert('Please enter a valid amount');
    return;
  }

  if (withdrawAmount < minWithdrawal) {
    alert(`Minimum withdrawal amount is $${minWithdrawal.toLocaleString()}`);
    return;
  }

  if (withdrawAmount > maxWithdrawal) {
    alert(`Maximum withdrawal amount is $${maxWithdrawal.toLocaleString()}`);
    return;
  }

  // Validate payment details
  if (method === 'bank_transfer') {
    const { accountName, accountNumber, ifscCode, bankName } = bankDetails;
    if (!accountName || !accountNumber || !ifscCode || !bankName) {
      alert('Please fill in all bank details');
      return;
    }
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscRegex.test(ifscCode)) {
      alert('Please enter a valid IFSC code');
      return;
    }
  } else if (method === 'upi') {
    if (!upiDetails.upiId) {
      alert('Please enter UPI ID');
      return;
    }
    const upiRegex = /^[\w.-]+@[\w.-]+$/;
    if (!upiRegex.test(upiDetails.upiId)) {
      alert('Please enter a valid UPI ID');
      return;
    }
  }

  setSubmitting(true);

  try {
    const result = await createWithdrawal({
      userId: currentUser.user.id,
      amount: withdrawAmount,
      mt5Login: selectedMt5Login || null,
      mt5Balance: accountInfo.mt5Balance,  // ⭐ PASS THE LIVE MT5 BALANCE
      withdrawalMethod: method,
      accountHolderName: method === 'bank_transfer' ? bankDetails.accountName : null,
      accountNumber: method === 'bank_transfer' ? bankDetails.accountNumber : null,
      ifscCode: method === 'bank_transfer' ? bankDetails.ifscCode : null,
      bankName: method === 'bank_transfer' ? bankDetails.bankName : null,
      upiId: method === 'upi' ? upiDetails.upiId : null
    });

    if (result.success) {
      alert('✅ Withdrawal request submitted successfully!\n\nWe will process it within 24-48 hours on business days.');
      
      await fetchData();
      resetForm();
    } else {
      alert(`❌ Error: ${result.error || 'Failed to submit withdrawal request'}`);
    }
  } catch (error) {
    console.error('Withdrawal error:', error);
    alert('❌ Failed to submit withdrawal request. Please try again or contact support.');
  } finally {
    setSubmitting(false);
  }
};

  // Reset form to initial state
  const resetForm = () => {
    setAmount('');
    setMethod('bank_transfer');
    setBankDetails({
      accountName: '',
      accountNumber: '',
      ifscCode: '',
      bankName: ''
    });
    setUpiDetails({ upiId: '' });
  };

  // Get status icon for withdrawal history
  const getStatusIcon = (status) => {
    switch(status?.toLowerCase()) {
      case 'approved':
        return <MdCheckCircle size={20} style={{ color: '#10b981' }} />;
      case 'pending':
        return <MdAccessTime size={20} style={{ color: '#f59e0b' }} />;
      case 'rejected':
        return <MdCancel size={20} style={{ color: '#ef4444' }} />;
      default:
        return <MdAccessTime size={20} style={{ color: '#6b7280' }} />;
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    const num = parseFloat(value || 0);
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Loading state
  if (loading) {
    return (
      <div className="withdraw-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading withdrawal information...</p>
        </div>
      </div>
    );
  }

  // Not logged in state
  if (!currentUser) {
    return (
      <div className="withdraw-container">
        <div className="login-prompt">
          <MdWarning size={48} style={{ color: '#f59e0b' }} />
          <h2>Authentication Required</h2>
          <p>Please login to access withdrawal features</p>
          <button 
            onClick={() => window.location.href = '/login'}
            className="btn btn-primary"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="withdraw-container">
      <div className="withdraw-header">
        <div>
          <h1>💸 Withdraw Funds</h1>
          <p>Request withdrawal from your trading account</p>
        </div>
      </div>

      <div className="withdraw-content">
        {/* Left Section - Withdrawal Form */}
        <div className="withdraw-form-section">
          
          {/* Account Balance Overview */}
          <div className="balance-card">
            <h3>💰 Account Balance Overview</h3>
            <div className="balance-grid">
              <div className="balance-item">
                <span className="balance-label">Wallet Balance</span>
                <span className="balance-value">${formatCurrency(accountInfo.walletBalance)}</span>
              </div>
              <div className="balance-item">
                <span className="balance-label">MT5 Balance</span>
                <span className="balance-value">${formatCurrency(accountInfo.mt5Balance)}</span>
              </div>
              <div className="balance-item">
                <span className="balance-label">Free Margin</span>
                <span className="balance-value">
                  {parseFloat(accountInfo.freeMargin || 0) < 0 ? '-$' : '$'}
                  {formatCurrency(Math.abs(parseFloat(accountInfo.freeMargin || 0)))}
                </span>
              </div>
              <div className="balance-item">
                <span className="balance-label">Open Positions</span>
                <span className="balance-value">{accountInfo.openPositions}</span>
              </div>
            </div>
            
            <div className="max-withdraw-info">
              <MdWarning size={18} />
              <span>
                <strong>Maximum Withdrawable:</strong> ${formatCurrency(maxWithdrawal)}
              </span>
            </div>

            {accountInfo.openPositions > 0 && (
              <div className="warning-notice">
                <MdWarning size={16} />
                <span>Close all positions to withdraw full balance</span>
              </div>
            )}
          </div>

          {/* Withdrawal Form */}
          <div className="form-card">
            <h2>📝 Withdrawal Request Form</h2>
            
            <form onSubmit={handleSubmit}>
              
              {/* MT5 Account Selection */}
              {mt5Accounts.length > 0 && (
                <div className="form-group">
                  <label htmlFor="mt5-account">
                    Select MT5 Account
                    <span className="required">*</span>
                  </label>
                  <select
                    id="mt5-account"
                    value={selectedAccountIndex}
                    onChange={handleAccountChange}
                    disabled={accountLoading}
                    required
                  >
                    {mt5Accounts.map((account, index) => (
                      <option key={account.id} value={index}>
                        {account.login_id} - Balance: ${formatCurrency(
  liveBalances[account.login_id] !== undefined 
    ? liveBalances[account.login_id] 
    : account.balance
)}
                      </option>
                    ))}
                  </select>
                  {accountLoading && <small className="loading-text">Loading account data...</small>}
                </div>
              )}

              {/* Withdrawal Amount */}
              <div className="form-group">
                <label htmlFor="amount">
                  Withdrawal Amount (USD)
                  <span className="required">*</span>
                </label>
                <input
                  id="amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`Enter amount (Min: $${minWithdrawal.toLocaleString()})`}
                  min={minWithdrawal}
                  max={maxWithdrawal}
                  step="0.01"
                  required
                />
                <small className="form-hint">
                  Min: ${minWithdrawal.toLocaleString()} | Max: ${formatCurrency(maxWithdrawal)}
                </small>
              </div>

              {/* Withdrawal Method */}
              <div className="form-group">
                <label htmlFor="method">
                  Withdrawal Method
                  <span className="required">*</span>
                </label>
                <select 
                  id="method"
                  value={method} 
                  onChange={(e) => setMethod(e.target.value)}
                  required
                >
                  <option value="bank_transfer">🏦 Bank Transfer</option>
                  <option value="upi">📱 UPI</option>
                </select>
              </div>

              {/* Bank Transfer Details */}
              {method === 'bank_transfer' && (
                <div className="payment-details-section">
                  <h3>🏦 Bank Account Details</h3>
                  
                  <div className="form-group">
                    <label htmlFor="account-name">
                      Account Holder Name
                      <span className="required">*</span>
                    </label>
                    <input
                      id="account-name"
                      type="text"
                      value={bankDetails.accountName}
                      onChange={(e) => setBankDetails({...bankDetails, accountName: e.target.value})}
                      placeholder="Enter full name as per bank account"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="account-number">
                      Account Number
                      <span className="required">*</span>
                    </label>
                    <input
                      id="account-number"
                      type="text"
                      value={bankDetails.accountNumber}
                      onChange={(e) => setBankDetails({...bankDetails, accountNumber: e.target.value})}
                      placeholder="Enter bank account number"
                      pattern="[0-9]{9,18}"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="ifsc-code">
                      IFSC Code
                      <span className="required">*</span>
                    </label>
                    <input
                      id="ifsc-code"
                      type="text"
                      value={bankDetails.ifscCode}
                      onChange={(e) => setBankDetails({...bankDetails, ifscCode: e.target.value.toUpperCase()})}
                      placeholder="e.g., HDFC0001234"
                      pattern="[A-Z]{4}0[A-Z0-9]{6}"
                      maxLength="11"
                      required
                    />
                    <small className="form-hint">11-character IFSC code</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="bank-name">
                      Bank Name
                      <span className="required">*</span>
                    </label>
                    <input
                      id="bank-name"
                      type="text"
                      value={bankDetails.bankName}
                      onChange={(e) => setBankDetails({...bankDetails, bankName: e.target.value})}
                      placeholder="Enter bank name"
                      required
                    />
                  </div>
                </div>
              )}

              {/* UPI Details */}
              {method === 'upi' && (
                <div className="payment-details-section">
                  <h3>📱 UPI Details</h3>
                  
                  <div className="form-group">
                    <label htmlFor="upi-id">
                      UPI ID
                      <span className="required">*</span>
                    </label>
                    <input
                      id="upi-id"
                      type="text"
                      value={upiDetails.upiId}
                      onChange={(e) => setUpiDetails({upiId: e.target.value})}
                      placeholder="username@paytm"
                      pattern="[\w.-]+@[\w.-]+"
                      required
                    />
                    <small className="form-hint">Enter your UPI ID (e.g., yourname@paytm)</small>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button 
                type="submit" 
                className="btn btn-primary btn-block"
                disabled={submitting || !accountInfo.canWithdraw || accountLoading}
              >
                {submitting ? (
                  <>
                    <span className="spinner-small"></span>
                    Processing...
                  </>
                ) : (
                  '💸 Submit Withdrawal Request'
                )}
              </button>
            </form>

            {/* Important Notice */}
            <div className="info-notice">
              <MdWarning size={20} />
              <div>
                <strong>Important Information:</strong>
                <ul>
                  <li>Withdrawals are processed within 24-48 hours on business days</li>
                  <li>Ensure your bank details are correct to avoid delays</li>
                  <li>You will receive a confirmation email once processed</li>
                  <li>Minimum withdrawal amount: ${minWithdrawal.toLocaleString()}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section - History & Info */}
        <div className="withdraw-history-section">
          
          {/* Withdrawal History */}
          <div className="history-card">
            <h2>📜 Withdrawal History</h2>
            
            {withdrawHistory.length === 0 ? (
              <div className="no-history">
                <MdAccountBalance size={48} style={{ color: '#cbd5e1' }} />
                <p>No withdrawal history yet</p>
                <small>Your withdrawal requests will appear here</small>
              </div>
            ) : (
              <div className="history-list">
                {withdrawHistory.map((withdrawal) => (
                  <div key={withdrawal.id} className="history-item">
                    <div className="history-icon">
                      {getStatusIcon(withdrawal.status)}
                    </div>
                    <div className="history-details">
                      <div className="history-amount">
                        ${formatCurrency(withdrawal.amount)}
                      </div>
                      <div className="history-method">
                        {withdrawal.withdrawal_method === 'bank_transfer' ? '🏦' : '📱'} 
                        {' '}
                        {withdrawal.withdrawal_method.replace('_', ' ').toUpperCase()}
                      </div>
                      <div className="history-date">
                        {new Date(withdrawal.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      {withdrawal.account_number && (
                        <div className="history-account">
                          A/C: ****{withdrawal.account_number.slice(-4)}
                        </div>
                      )}
                      {withdrawal.upi_id && (
                        <div className="history-account">
                          UPI: {withdrawal.upi_id}
                        </div>
                      )}
                    </div>
                    <div className="history-status">
                      <span className={`status-badge status-${withdrawal.status?.toLowerCase() || 'pending'}`}>
                        {withdrawal.status?.toUpperCase() || 'PENDING'}
                      </span>
                      {withdrawal.status === 'rejected' && withdrawal.rejection_reason && (
                        <small className="rejection-reason">
                          Reason: {withdrawal.rejection_reason}
                        </small>
                      )}
                      {withdrawal.status === 'approved' && withdrawal.processed_at && (
                        <small className="processed-date">
                          Processed: {new Date(withdrawal.processed_at).toLocaleDateString()}
                        </small>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Info Card */}
          <div className="quick-info-card">
            <h3>💡 Quick Information</h3>
            <ul>
              <li>
                <strong>Minimum Withdrawal:</strong> ${minWithdrawal.toLocaleString()}
              </li>
              <li>
                <strong>Processing Time:</strong> 24-48 hours
              </li>
              <li>
                <strong>Withdrawal Fees:</strong> No fees
              </li>
              <li>
                <strong>Business Hours:</strong> Mon-Fri, 9 AM - 6 PM
              </li>
              <li>
                <strong>Support:</strong> Available 24/7
              </li>
            </ul>
          </div>

          {/* Tips Card */}
          <div className="tips-card">
            <h3>💭 Pro Tips</h3>
            <ul>
              <li>Close all open positions to withdraw your full balance</li>
              <li>Double-check your bank details before submitting</li>
              <li>Keep your UPI ID active and verified</li>
              <li>Contact support if withdrawal is delayed beyond 48 hours</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}